"use client";

import { FormEvent, useId, useState } from "react";
import { emailRegExp } from "../utils/emailValidationRegex";
import styles from "./HomeSubscribe.module.css";

type Status = "idle" | "loading" | "success";

type ShareCodeSubscribeProps = {
  id?: string;
  url: string;
  codeName: string;
};

function getSubmitLabel(status: Status): string {
  switch (status) {
    case "idle":
      return "Get the sample";
    case "loading":
      return "Sending";
    case "success":
      return "Get the sample";
    default: {
      const _never: never = status;
      return _never;
    }
  }
}

export function ShareCodeSubscribe({
  id,
  url,
  codeName,
}: ShareCodeSubscribeProps) {
  const generatedId = useId();
  const formId = id ?? generatedId;
  const emailFieldId = `${formId}-email`;
  const errorId = `${formId}-error`;

  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (status === "loading") return;

    emailRegExp.lastIndex = 0;
    if (!emailRegExp.test(email.trim())) {
      setError("Enter a valid email address.");
      return;
    }

    setError(null);
    setStatus("loading");

    try {
      const response = await fetch("/api/shareCode", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          url,
          codeName,
        }),
      });

      if (!response.ok) {
        const payload = (await response.json().catch(() => null)) as
          | { error?: string }
          | null;
        setError(payload?.error ?? "Something went wrong. Try again in a moment.");
        setStatus("idle");
        return;
      }

      setEmail("");
      setStatus("success");
    } catch {
      setError("Something went wrong. Try again in a moment.");
      setStatus("idle");
    }
  }

  if (status === "success") {
    return (
      <div className={styles.wrap}>
        <p className={`${styles.success} ${styles.light}`} role="status">
          You&apos;re in. Check your inbox for the sample, and spam, just in
          case.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.wrap}>
      <form
        id={formId}
        className={`${styles.form} ${styles.light} ${styles.stacked}`}
        onSubmit={onSubmit}
        noValidate
        aria-busy={status === "loading"}
      >
        <label htmlFor={emailFieldId} className={styles.srOnly}>
          Email address
        </label>
        <input
          id={emailFieldId}
          name="email"
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="Email address"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            if (error) setError(null);
          }}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
          disabled={status === "loading"}
        />
        <button type="submit" disabled={!email.trim() || status === "loading"}>
          {getSubmitLabel(status)}
        </button>
      </form>
      {error ? (
        <p id={errorId} className={`${styles.error} ${styles.light}`} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
